// homesApi.js — the Homes section's I/O. The list goes through figApi
// (Homes_Watch is in LIST_DEFINITIONS, so writes are allowlisted and date
// columns are guarded). Photos go browser → Graph into the Homes_Photos
// library with the signed-in user's token; Netlify and the App Service never
// see the bytes. No HAR fetch anywhere: prices change only when Claude in chat
// sends new facts to the server's homes_* tools.

import { getItems, updateItem, HOMES_LISTS } from '../api/figApi';
import { sniffImageType, PHOTO_MAX_BYTES, SIMPLE_PUT_LIMIT, UPLOAD_CHUNK } from '../contacts/photoNames';
import { nextHomePhotoName } from './homesCalc';

const GRAPH = 'https://graph.microsoft.com/v1.0';
const SITE = 'fragosinsurancegroupllc.sharepoint.com:/sites/DefineHolyFamily';
export const PHOTO_LIBRARY = 'Homes_Photos';
const MIME = { jpg: 'image/jpeg', png: 'image/png' };

export const loadHomes = (token) => getItems(token, HOMES_LISTS.WATCH);

/** Patch hand-edited fields on one row. keepalive for writes flushed on page unload. */
export const patchHome = (token, itemId, fields, opts) => updateItem(token, HOMES_LISTS.WATCH, itemId, fields, opts);

async function httpError(res, what) {
  let msg = res.statusText;
  try { const j = await res.json(); msg = j?.error?.message || msg; } catch { /* non-JSON */ }
  const e = new Error(`${what} (${res.status})${msg ? `: ${msg}` : ''}`);
  e.status = res.status;
  return e;
}

let driveIdCache = null;
async function photoDriveId(token) {
  if (driveIdCache) return driveIdCache;
  const h = { Authorization: `Bearer ${token}` };
  const site = await fetch(`${GRAPH}/sites/${SITE}?$select=id`, { headers: h });
  if (!site.ok) throw await httpError(site, 'Site unavailable');
  const siteId = (await site.json()).id;
  const lists = await fetch(`${GRAPH}/sites/${siteId}/lists?$select=id,displayName&$top=500`, { headers: h });
  if (!lists.ok) throw await httpError(lists, 'Lists unavailable');
  const lib = ((await lists.json()).value || []).find((l) => l.displayName === PHOTO_LIBRARY);
  if (!lib) throw new Error(`${PHOTO_LIBRARY} library not found`);
  const drive = await fetch(`${GRAPH}/sites/${siteId}/lists/${lib.id}/drive?$select=id`, { headers: h });
  if (!drive.ok) throw await httpError(drive, 'Photo library drive unavailable');
  driveIdCache = (await drive.json()).id;
  return driveIdCache;
}

/**
 * file name → { thumb, full }. Graph URLs are pre-authenticated and expire in
 * about an hour, so this is re-listed on every load and kept in state only.
 * No $select on the listing (it drops the download-URL annotation).
 * Returns an empty Map on any failure; never throws.
 */
export async function loadHomePhotos(token) {
  try {
    const driveId = await photoDriveId(token);
    const out = new Map();
    let url = `${GRAPH}/drives/${driveId}/root/children?$expand=thumbnails&$top=500`;
    while (url) {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Photo listing failed (${res.status})`);
      const d = await res.json();
      for (const it of d.value || []) {
        if (!it.file) continue;
        const t = it.thumbnails?.[0] || {};
        out.set(it.name, { thumb: t.large?.url || t.medium?.url || null, full: it['@microsoft.graph.downloadUrl'] || null });
      }
      url = d['@odata.nextLink'] || null;
    }
    return out;
  } catch {
    return new Map();
  }
}

async function readHead(file) {
  const blob = file.slice(0, 4);
  const buf = typeof blob.arrayBuffer === 'function' ? await blob.arrayBuffer() : await new Response(blob).arrayBuffer();
  return new Uint8Array(buf);
}

async function simplePut(token, driveId, name, file, mime) {
  const res = await fetch(
    `${GRAPH}/drives/${driveId}/root:/${encodeURIComponent(name)}:/content?@microsoft.graph.conflictBehavior=fail`,
    { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': mime }, body: file },
  );
  if (!res.ok) throw await httpError(res, 'Upload failed');
  return res.json();
}

// > 4 MB (most phone photos): an upload session in 5 MiB chunks.
async function sessionPut(token, driveId, name, file) {
  const sess = await fetch(
    `${GRAPH}/drives/${driveId}/root:/${encodeURIComponent(name)}:/createUploadSession`,
    { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ item: { '@microsoft.graph.conflictBehavior': 'fail' } }) },
  );
  if (!sess.ok) throw await httpError(sess, 'Upload session failed');
  const { uploadUrl } = await sess.json();
  let start = 0, last = null;
  while (start < file.size) {
    const end = Math.min(start + UPLOAD_CHUNK, file.size);
    const res = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Range': `bytes ${start}-${end - 1}/${file.size}` }, body: file.slice(start, end) });
    if (!res.ok && res.status !== 202) throw await httpError(res, 'Chunk upload failed');
    if (res.status === 200 || res.status === 201) last = await res.json();
    start = end;
  }
  return last;
}

/**
 * Upload one photo for a house and return its file name (the PhotoRef value).
 * Name from nextHomePhotoName over the current library names; conflictBehavior
 * FAIL, so a stale listing can never overwrite. On 409 the library is
 * re-listed and the upload retried once. Type from the first bytes, not
 * file.type (Safari lies). The caller writes PhotoRef.
 */
export async function uploadHomePhoto(token, homeId, file, existingNames) {
  if (!file || typeof file.size !== 'number') throw new Error('No file selected.');
  if (file.size === 0) throw new Error('The file is empty.');
  if (file.size > PHOTO_MAX_BYTES) throw new Error('Photo is too large (over 20 MB).');
  const ext = sniffImageType(await readHead(file));
  if (!ext) throw new Error('Only JPEG or PNG (iPhone HEIC: share as "Most Compatible").');
  const driveId = await photoDriveId(token);
  const put = (name) => (file.size <= SIMPLE_PUT_LIMIT ? simplePut(token, driveId, name, file, MIME[ext]) : sessionPut(token, driveId, name, file));
  let name = nextHomePhotoName(homeId, existingNames, ext);
  let item;
  try {
    item = await put(name);
  } catch (e) {
    if (e?.status !== 409) throw e;
    name = nextHomePhotoName(homeId, [...(await loadHomePhotos(token)).keys()], ext);
    item = await put(name);
  }
  return item?.name || name;
}
